import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardActions,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    IconButton,
    Chip,
    Grid,
    Switch,
    FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";

import MobileContainer from "../global/components/MobileContainer";
import { ProductsLoader } from "../components/ProductsLoader";
import { useProducts } from "../hooks/useProducts";
import { useShoppingListRedux } from "../hooks/useShoppingListRedux";
import { MAIN_COLOR_RED } from "../assets/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { SavedShoppingList } from "../redux/shoppingListSlice";

const ShoppingListPage: React.FC = () => {
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [listName, setListName] = useState("");
    const [editingListId, setEditingListId] = useState<string | null>(null);
    const [isCreatingEmptyList, setIsCreatingEmptyList] = useState(false);
    const isTogglingRef = useRef<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    const {
        shoppingList,
        savedLists,
        saveList,
        loadList,
        deleteList,
        editList,
        clearCurrentList,
        loadSavedLists,
        setListActive,
    } = useShoppingListRedux();

    const { products } = useProducts();

    // Ensure saved lists are loaded when component mounts
    useEffect(() => {
        loadSavedLists();
    }, [loadSavedLists]);


    // Handle navigation state for loading lists
    useEffect(() => {
        if (location.state?.loadList) {
            const listToLoad = location.state.loadList;
            loadList(listToLoad);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, navigate, location.pathname, loadList]);

    const handleSaveList = () => {
        if (!listName.trim()) return;
        if (isCreatingEmptyList && !editingListId) {
            // Create empty list via backend (same flow as create with items)
            saveList(listName.trim(), undefined, {});
        } else {
            // Save current shopping list or edit existing list
            saveList(listName.trim(), editingListId || undefined);
        }
        setListName("");
        setEditingListId(null);
        setIsCreatingEmptyList(false);
        setSaveDialogOpen(false);
    };

    const handleLoadList = (list: SavedShoppingList) => {
        loadList(list);
        navigate('/cart');
    };

    const handleDeleteList = (listId: string) => {
        if (window.confirm("Are you sure you want to delete this shopping list?")) {
            deleteList(listId);
        }
    };

    const handleEditList = (list: SavedShoppingList) => {
        const editInfo = editList(list);
        setListName(editInfo.name);
        setEditingListId(editInfo.id);
        setSaveDialogOpen(true);
    };

    const openSaveDialog = (isEmptyList: boolean = false) => {
        setListName("");
        setEditingListId(null);
        setIsCreatingEmptyList(isEmptyList);
        setSaveDialogOpen(true);
    };

    const getListItemsCount = (list: SavedShoppingList): number => {
        return Object.values(list.items).reduce((sum, qty) => sum + qty, 0);
    };

    const getListTotalPrice = (list: SavedShoppingList): number => {
        return Object.entries(list.items).reduce((sum, [productId, quantity]) => {
            const product = products.find(p => p.unique_id === productId);
            if (product) {
                return sum + (parseFloat(product.sale_price) || 0) * quantity;
            }
            return sum;
        }, 0);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    // Sync active status when shopping list changes (but not during manual toggles)
    useEffect(() => {
        // Skip if we're in the middle of a toggle operation
        if (isTogglingRef.current !== null) {
            // Clear the ref after state settles - longer timeout to ensure Redux state has updated
            const timer = setTimeout(() => {
                isTogglingRef.current = null;
            }, 300);
            return () => clearTimeout(timer);
        }
        
        // Find which saved list matches the current shopping list
        const matchingList = savedLists.find(list => {
            if (Object.keys(shoppingList).length === 0 || Object.keys(list.items).length === 0) {
                return false;
            }
            
            const currentListKeys = Object.keys(shoppingList).sort();
            const listKeys = Object.keys(list.items).sort();
            
            if (currentListKeys.length !== listKeys.length) {
                return false;
            }
            
            return currentListKeys.every(key => 
                listKeys.includes(key) && 
                Number(shoppingList[key]) === Number(list.items[key])
            );
        });
        
        // Update active status based on matching list
        if (matchingList && !matchingList.active) {
            // If we have a matching list that's not active, set it as active
            setListActive(matchingList.id, true);
        } else if (!matchingList) {
            // If no matching list, ensure all lists are inactive
            savedLists.forEach(list => {
                if (list.active) {
                    setListActive(list.id, false);
                }
            });
        }
    }, [shoppingList, savedLists, setListActive]);

    // Check if a list is currently active - use active field from data model
    const isListActive = (list: SavedShoppingList): boolean => {
        return list.active === true;
    };

    // Handle toggle switch to activate/deactivate list
    const handleToggleList = (list: SavedShoppingList, checked: boolean) => {
        if (checked) {
            // Mark that we're toggling this list
            isTogglingRef.current = list.id;
            // Set this list as active (this will set all others to inactive)
            setListActive(list.id, true);
            // Load the selected list (this will replace the current list)
            loadList(list);
        } else {
            // Mark that we're clearing
            isTogglingRef.current = 'clear';
            // Set this list as inactive
            setListActive(list.id, false);
            // Clear the current list
            clearCurrentList();
        }
    };

    return (
        <ProductsLoader>
            <MobileContainer>
                <Box sx={{ pb: 8 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
                        <Typography variant="h5" fontWeight="bold">
                            Shopping Lists
                        </Typography>
                        <IconButton
                            onClick={() => openSaveDialog(true)}
                            sx={{ 
                                backgroundColor: MAIN_COLOR_RED, 
                                color: "white",
                                minWidth: "40px",
                                width: "40px",
                                height: "40px",
                                '&:hover': {
                                    backgroundColor: MAIN_COLOR_RED,
                                },
                                '&:active': {
                                    backgroundColor: MAIN_COLOR_RED,
                                }
                            }}
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>

                    {Object.keys(shoppingList).length > 0 && (() => {
                        const totalCurrentItems = Object.values(shoppingList).reduce(
                            (sum, qty) => sum + Number(qty || 0),
                            0
                        );
                        return (
                        <Card sx={{ mb: 3, backgroundColor: '#f5f5f5' }}>
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold">
                                            Current Shopping List
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {totalCurrentItems} items
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => openSaveDialog(false)}
                                            sx={{ 
                                                borderColor: MAIN_COLOR_RED,
                                                color: MAIN_COLOR_RED,
                                                '&:hover': {
                                                    borderColor: MAIN_COLOR_RED,
                                                    backgroundColor: 'rgba(220, 38, 38, 0.04)',
                                                }
                                            }}
                                        >
                                            Save List
                                        </Button>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                if (window.confirm("Are you sure you want to clear the current shopping list?")) {
                                                    clearCurrentList();
                                                }
                                            }}
                                            sx={{ 
                                                color: 'error.main',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(244, 67, 54, 0.04)',
                                                }
                                            }}
                                            title="Clear current shopping list"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                        );
                    })()}

                    {(!savedLists || savedLists.length === 0) ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                textAlign: 'center',
                            }}
                        >
                            <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                No shopping lists yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Create your first shopping list to get started
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Tip: Save your current shopping list using the "Save List" button above
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {savedLists.map((list: SavedShoppingList) => {
                                const itemsCount = getListItemsCount(list);
                                const totalPrice = getListTotalPrice(list);
                                const isCompleted = list.is_completed === true;

                                return (
                                    <Grid item xs={12} key={list.id}>
                                        <Card
                                            sx={{
                                                cursor: isCompleted ? 'default' : 'pointer',
                                                opacity: isCompleted ? 0.85 : 1,
                                                '&:hover': {
                                                    boxShadow: isCompleted ? 2 : 4,
                                                },
                                                transition: 'box-shadow 0.3s',
                                            }}
                                            onClick={() => {
                                                if (isCompleted) return;
                                                navigate(`/lists/edit/${list.id}`);
                                            }}
                                        >
                                            <CardContent>
                                                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                                            <Typography variant="h6" fontWeight="bold">
                                                                {list.name}
                                                            </Typography>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                {isCompleted ? (
                                                                    <Chip
                                                                        label="Completed"
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: "#16a34a",
                                                                            color: "#fff",
                                                                            fontWeight: "bold",
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Switch
                                                                                key={`switch-${list.id}-${list.active ? 'active' : 'inactive'}`}
                                                                                checked={isListActive(list)}
                                                                                onChange={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleToggleList(list, e.target.checked);
                                                                                }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                sx={{
                                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                                        color: MAIN_COLOR_RED,
                                                                                    },
                                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                                        backgroundColor: MAIN_COLOR_RED,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        }
                                                                        label="Active"
                                                                        labelPlacement="start"
                                                                        sx={{ margin: 0 }}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                )}
                                                                <ChevronRightIcon 
                                                                    sx={{ 
                                                                        color: 'text.secondary',
                                                                        fontSize: 28,
                                                                    }} 
                                                                />
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                                                            <Chip 
                                                                label={`${itemsCount} items`}
                                                                size="small"
                                                                sx={{ backgroundColor: MAIN_COLOR_RED, color: "white" }}
                                                            />
                                                            <Chip 
                                                                label={`€${totalPrice.toFixed(2)}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                                            {isCompleted ? "Completed" : "Updated"}: {formatDate(list.updatedAt)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                
                                                {/* List Items Preview */}
                                                {itemsCount > 0 && (
                                                    <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: '#f9f9f9', borderRadius: 1, p: 1 }}>
                                                        {Object.entries(list.items).slice(0, 3).map(([productId, quantity]) => {
                                                            const product = products.find(p => p.unique_id === productId);
                                                            if (!product) return null;
                                                            
                                                            return (
                                                                <ListItem key={productId} sx={{ px: 1, py: 0.5 }}>
                                                                    <ListItemAvatar>
                                                                        <Avatar
                                                                            variant="square"
                                                                            src={product.image_url}
                                                                            alt={product.title}
                                                                            sx={{ width: 32, height: 32 }}
                                                                        />
                                                                    </ListItemAvatar>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Typography variant="body2" noWrap>
                                                                                {product.title}
                                                                            </Typography>
                                                                        }
                                                                        secondary={
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                Qty: {quantity}
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            );
                                                        })}
                                                        {itemsCount > 3 && (
                                                            <ListItem sx={{ px: 1, py: 0.5 }}>
                                                                <ListItemText>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        +{itemsCount - 3} more items
                                                                    </Typography>
                                                                </ListItemText>
                                                            </ListItem>
                                                        )}
                                                    </List>
                                                )}
                                            </CardContent>
                                            <CardActions sx={{ justifyContent: "flex-end", pt: 0 }} onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    size="small"
                                                    startIcon={<ShoppingCartIcon />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleLoadList(list);
                                                    }}
                                                    sx={{ 
                                                        color: MAIN_COLOR_RED,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(220, 38, 38, 0.04)',
                                                        }
                                                    }}
                                                >
                                                    Load
                                                </Button>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!list.is_completed) {
                                                            handleEditList(list);
                                                        }
                                                    }}
                                                    sx={{ 
                                                        color: MAIN_COLOR_RED,
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(220, 38, 38, 0.04)',
                                                        }
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteList(list.id);
                                                    }}
                                                    sx={{ 
                                                        color: 'error.main',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(244, 67, 54, 0.04)',
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            </MobileContainer>

            {/* Save List Dialog */}
            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingListId ? "Edit Shopping List" : (isCreatingEmptyList ? "Create New Shopping List" : "Save Shopping List")}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="List Name"
                        fullWidth
                        variant="outlined"
                        value={listName}
                        onChange={(e) => setListName(e.target.value)}
                        placeholder="Enter a name for your shopping list"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleSaveList} 
                        variant="contained"
                        disabled={!listName.trim()}
                        sx={{ backgroundColor: MAIN_COLOR_RED }}
                    >
                        {editingListId ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </ProductsLoader>
    );
};

export default ShoppingListPage;

